import { useState, useEffect } from "react";
import { API } from "../../../api";
import "../../../styles/admin/CalendarEditor.css";

const DAYS = [
	{ id: 1, name: "Lundi" },
	{ id: 2, name: "Mardi" },
	{ id: 3, name: "Mercredi" },
	{ id: 4, name: "Jeudi" },
	{ id: 5, name: "Vendredi" },
	{ id: 6, name: "Samedi" },
	{ id: 7, name: "Dimanche" }
];

const formatDateForBackend = (dateString) => {
	if (!dateString) return null;
	return dateString + 'T00:00:00.000Z';
};

const formatDateForDisplay = (dateString) => {
	if (!dateString) return '';
	const [y, m, d] = dateString.split('-').map(Number);
	const utcDate = new Date(Date.UTC(y, m - 1, d));
	return utcDate.toLocaleDateString('fr-FR', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		timeZone: 'UTC'
	});
};

export default function CalendarEditor() {
	const today = new Date().toISOString().split('T')[0];
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(null);
	const [status, setStatus] = useState(null);
	const [openingHours, setOpeningHours] = useState([]);
	const [exceptions, setExceptions] = useState([]);
	const [editingHour, setEditingHour] = useState(null);
	const [editingException, setEditingException] = useState(null);
	const [editingExceptionSlot, setEditingExceptionSlot] = useState(null);
	const [newException, setNewException] = useState(null);

	useEffect(() => {
		const loadData = async () => {
			try {
				setLoading(true);
				const hoursData = await API.get("calendar");
				let exceptionsData = [];

				try {
					exceptionsData = await API.get("calendarExeption");
				} catch (err) {
					console.error("Exceptions non disponibles:", err);
				}
				
				const parsedHours = hoursData.map(h => ({
					...h,
					slots: typeof h.slots === 'string' ? JSON.parse(h.slots) : h.slots
				}));
				
				const allDays = DAYS.map(day => {
					const existing = parsedHours.find(h => h.day_of_week === day.id);
					return existing || { day_of_week: day.id, slots: [] };
				});
				
				const parsedExceptions = (exceptionsData || []).map(e => {
					let dateValue = e.date;
					if (e.date) {
						const d = new Date(e.date);
						const localIso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
						dateValue = localIso.split('T')[0];
					}
					return {
						...e,
						date: dateValue,
						slots: e.slots && typeof e.slots === 'string' ? JSON.parse(e.slots) : e.slots
					};
				});
				
				setOpeningHours(allDays);
				setExceptions(parsedExceptions);
			} catch (error) {
				console.error("Erreur lors du chargement:", error);
				setStatus({ type: "error", message: error.message || "Erreur de chargement" });
				const defaultDays = DAYS.map(day => ({ day_of_week: day.id, slots: [] }));
				setOpeningHours(defaultDays);
				setExceptions([]);
			} finally {
				setLoading(false);
			}
		};
		
		loadData();
	}, []);

	const getHoursForDay = (dayId) => {
		const dayData = openingHours.find(h => h.day_of_week === dayId);
		return dayData ? dayData.slots : [];
	};

	const getDayId = (dayOfWeek) => {
		const dayData = openingHours.find(h => h.day_of_week === dayOfWeek);
		return dayData?.id;
	};

	const isDateUsed = (dateString, excludeId = null) => {
		return exceptions.some(ex => ex.date === dateString && ex.id !== excludeId);
	};

	const getDateError = (dateString, excludeId = null) => {
		if (!dateString) return null;
		if (dateString < today) return "Impossible de choisir une date passée";
		if (isDateUsed(dateString, excludeId)) return "Une exception existe déjà pour cette date";
		return null;
	};

	const getUsedDates = () => {
		return exceptions.map(ex => ex.date).sort();
	};

	const handleSaveSlot = async (dayOfWeek, slotIndex) => {
		const dayId = getDayId(dayOfWeek);
		const dayData = openingHours.find(h => h.day_of_week === dayOfWeek);
		setSaving(`day-${dayOfWeek}`);
		
		try {
			await API.put("calendar", { id: dayId }, { day_of_week: dayOfWeek, slots: JSON.stringify(dayData.slots) } );
			
			setEditingHour(null);
			setStatus({ type: "success", message: "Horaire mis à jour" });
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur lors de la sauvegarde:", error);
			setStatus({ type: "error", message: error.message || "Erreur de sauvegarde" });
			setTimeout(() => setStatus(null), 3000);
		} finally {
			setSaving(null);
		}
	};

	const handleDeleteSlot = async (dayOfWeek, slotIndex) => {
		if (!window.confirm("Supprimer cet horaire ?")) return;
		const dayId = getDayId(dayOfWeek);
		setSaving(`day-${dayOfWeek}`);
		const updatedSlots = openingHours.find(h => h.day_of_week === dayOfWeek).slots.filter((_, i) => i !== slotIndex);
		
		try {
			await API.put("calendar", 
				{ id: dayId },
				{ 
					day_of_week: dayOfWeek, 
					slots: JSON.stringify(updatedSlots)
				}
			);
			
			setOpeningHours(prev => prev.map(h => 
				h.day_of_week === dayOfWeek ? { ...h, slots: updatedSlots } : h
			));
			setStatus({ type: "success", message: "Horaire supprimé" });
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur lors de la suppression:", error);
			setStatus({ type: "error", message: error.message || "Erreur de suppression" });
			setTimeout(() => setStatus(null), 3000);
		} finally {
			setSaving(null);
		}
	};

	const handleAddSlot = (dayOfWeek) => {
		const newSlot = { opens_at: "09:00", closes_at: "18:00" };
		setOpeningHours(prev => prev.map(h => 
			h.day_of_week === dayOfWeek  ? { ...h, slots: [...h.slots, newSlot] } : h
		));
		const dayData = openingHours.find(h => h.day_of_week === dayOfWeek);
		const newIndex = dayData.slots.length;
		setEditingHour(`${dayOfWeek}-${newIndex}`);
	};

	const handleUpdateSlot = (dayOfWeek, slotIndex, field, value) => {
		setOpeningHours(prev => prev.map(h => 
			h.day_of_week === dayOfWeek
				? {
					...h,
					slots: h.slots.map((s, i) => 
						i === slotIndex ? { ...s, [field]: value } : s
					)
				}
				: h
		));
	};

	const handleAddExceptionSlot = (exceptionId) => {
		const newSlot = { opens_at: "09:00", closes_at: "18:00" };
		const exception = exceptions.find(ex => ex.id === exceptionId);
		const newIndex = (exception.slots || []).length;
		setExceptions(prev => prev.map(ex => 
			ex.id === exceptionId ? { ...ex, slots: [...(ex.slots || []), newSlot] } : ex
		));
		setEditingExceptionSlot(`${exceptionId}-${newIndex}`);
	};

	const handleDeleteExceptionSlot = (exceptionId, slotIndex) => {
		setExceptions(prev => prev.map(ex => 
			ex.id === exceptionId ? { ...ex, slots: ex.slots.filter((_, i) => i !== slotIndex) } : ex
		));
	};

	const handleUpdateExceptionSlot = (exceptionId, slotIndex, field, value) => {
		setExceptions(prev => prev.map(ex => 
			ex.id === exceptionId 
				? { 
					...ex, 
					slots: ex.slots.map((s, i) => 
						i === slotIndex ? { ...s, [field]: value } : s
					) 
				}
				: ex
		));
	};

	const handleSaveException = async (exception) => {
		if (isDateUsed(exception.date, exception.id)) {
			setStatus({ type: "error", message: "Une exception existe déjà pour cette date" });
			setTimeout(() => setStatus(null), 3000);
			return;
		}
		
		setSaving(`exception-${exception.id || 'new'}`);
		
		try {
			const exceptionData = {
				date: formatDateForBackend(exception.date),
				slots: exception.slots ? JSON.stringify(exception.slots) : null,
				comment: exception.comment
			};
			console.log('exceptionData', exceptionData);
			console.log('exception', exception);
			if (exception.id) {
				await API.put("calendarExeption", { id: exception.id }, exceptionData);
				setExceptions(prev => prev.map(e => e.id === exception.id ? exception : e));
			} else {
				const result = await API.post("calendarExeption", exceptionData);
				const newId = (result && result.id) || (Array.isArray(result) && result[0] && result[0].id) || Date.now();
				setExceptions(prev => [...prev, { ...exception, id: newId }]);
			}
			
			setEditingException(null);
			setNewException(null);
			setStatus({ type: "success", message: "Exception enregistrée" });
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur lors de la sauvegarde de l'exception:", error);
			setStatus({ type: "error", message: error.message || "Erreur de sauvegarde" });
			setTimeout(() => setStatus(null), 3000);
		} finally {
			setSaving(null);
		}
	};

	const handleDeleteException = async (exceptionId) => {
		if (!window.confirm("Supprimer cette exception ?")) return;
		setSaving(`exception-${exceptionId}`);
		
		try {
			await API.delete("calendarExeption", { id: exceptionId });
			setExceptions(prev => prev.filter(e => e.id !== exceptionId));
			setStatus({ type: "success", message: "Exception supprimée" });
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur lors de la suppression de l'exception:", error);
			setStatus({ type: "error", message: error.message || "Erreur de suppression" });
			setTimeout(() => setStatus(null), 3000);
		} finally {
			setSaving(null);
		}
	};

	if (loading) {
		return (
			<div className="cal-editor-container">
				<div className="cal-loader">
					<div className="cal-spinner"></div>
					<p>Chargement du calendrier...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="cal-editor-container">
			<div className="cal-header-section">
				<h1 className="cal-title">Calendrier d'Ouverture</h1>
				<p className="cal-subtitle">Gérez les horaires hebdomadaires et les exceptions</p>
			</div>

			{status && (
				<div className={`cal-status cal-status-${status.type}`}>
					<div className="cal-status-content">
						<span className="cal-status-icon">
							{status.type === "success" ? "✓" : status.type === "error" ? "✕" : "ℹ"}
						</span>
						<span>{status.message}</span>
					</div>
				</div>
			)}

			<div className="cal-content">
				{/* Horaires Hebdomadaires */}
				<div className="cal-section">
					<h2 className="cal-section-title">Horaires Hebdomadaires</h2>
					<div className="cal-week-grid">
						{DAYS.map(day => {
							const slots = getHoursForDay(day.id);
							return (
								<div key={day.id} className="cal-day-card">
									<div className="cal-day-header">
										<h3>{day.name}</h3>
										<button 
											className="cal-btn-add"
											onClick={() => handleAddSlot(day.id)}
											title="Ajouter un créneau"
										>
											+ Créneau
										</button>
									</div>
									<div className="cal-day-slots">
										{slots.length === 0 ? (
											<p className="cal-empty">Fermé</p>
										) : (
											slots.map((slot, slotIndex) => (
												<div key={slotIndex} className="cal-slot">
													{editingHour === `${day.id}-${slotIndex}` ? (
														<div className="cal-slot-edit">
															<input
																type="time"
																value={slot.opens_at}
																onChange={(e) => handleUpdateSlot(day.id, slotIndex, 'opens_at', e.target.value)}
																className="cal-input-time"
															/>
															<span>→</span>
															<input
																type="time"
																value={slot.closes_at}
																onChange={(e) => handleUpdateSlot(day.id, slotIndex, 'closes_at', e.target.value)}
																className="cal-input-time"
															/>
															<button
																onClick={() => handleSaveSlot(day.id, slotIndex)}
																className="cal-btn-save-inline"
																disabled={saving === `day-${day.id}`}
															>
																{saving === `day-${day.id}` ? "..." : "✓"}
															</button>
															<button
																onClick={() => setEditingHour(null)}
																className="cal-btn-cancel-inline"
															>
																✕
															</button>
														</div>
													) : (
														<div className="cal-slot-view">
															<span className="cal-slot-time">{slot.opens_at} - {slot.closes_at}</span>
															<div className="cal-slot-actions">
																<button
																	onClick={() => setEditingHour(`${day.id}-${slotIndex}`)}
																	className="cal-btn-edit-small"
																	title="Modifier"
																>
																	✎
																</button>
																<button
																	onClick={() => handleDeleteSlot(day.id, slotIndex)}
																	className="cal-btn-delete-small"
																	title="Supprimer"
																	disabled={saving === `day-${day.id}`}
																>
																	🗑
																</button>
															</div>
														</div>
													)}
												</div>
											))
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>

				{/* Exceptions */}
				<div className="cal-section">
					<div className="cal-section-header">
						<h2 className="cal-section-title">Exceptions</h2>
						<button className="cal-btn-add-exception" onClick={() => setNewException({ date: new Date().toISOString().split('T')[0], is_closed: false, slots: [{ opens_at: "09:00", closes_at: "18:00" }], comment: "" })} >+ Nouvelle Exception</button>
					</div>

					{newException && (
						<div className="cal-exception-form">
							<h3>Nouvelle Exception</h3>
							<div className="cal-form-grid">
								<div className="cal-form-field">
									<label>Date</label>
									<input type="date" value={newException.date} onChange={(e) => setNewException({ ...newException, date: e.target.value })} className="cal-input" min={today} />
									{getDateError(newException.date) && (
										<small style={{ color: 'red', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>{getDateError(newException.date)} </small>
									)}
								</div>
								<div className="cal-form-field">
									<label>
										<input
											type="checkbox"
										checked={newException.slots === null}
										onChange={(e) => setNewException({ 
											...newException, 
											slots: e.target.checked ? null : [{ opens_at: "09:00", closes_at: "18:00" }]
										})}
										/>
										Fermé toute la journée
									</label>
								</div>
								<div className="cal-form-field cal-form-field-full">
									<label>Commentaire</label>
									<input type="text" value={newException.comment} onChange={(e) => setNewException({ ...newException, comment: e.target.value })} className="cal-input" placeholder="Ex: Férié, fermeture exceptionnelle..." />
								</div>
							</div>
							
						{newException.slots !== null && (
							<div className="cal-exception-slots">
								<div className="cal-slots-header">
									<h4>Créneaux horaires</h4>
									<button type="button" className="cal-btn-add-slot" onClick={() => setNewException({ ...newException, slots: [...(newException.slots || []), { opens_at: "09:00", closes_at: "18:00" }] })} >+ Créneau</button>
								</div>
								<div className="cal-slots-list">
									{(newException.slots || []).map((slot, index) => (
									<div key={index} className="cal-slot-row">
											<span className="cal-slot-label">Créneau {index + 1}</span>
											<input
												type="time"
												value={slot.opens_at}
												onChange={(e) => setNewException({
													...newException,
													slots: newException.slots.map((s, i) => 
														i === index ? { ...s, opens_at: e.target.value } : s
													)
												})}
												className="cal-input-time"
											/>
											<span>→</span>
											<input
												type="time"
												value={slot.closes_at}
												onChange={(e) => setNewException({
													...newException,
													slots: newException.slots.map((s, i) => 
														i === index ? { ...s, closes_at: e.target.value } : s
													)
												})}
												className="cal-input-time"
											/>
											<button type="button" className="cal-btn-delete-slot" onClick={() => setNewException({ ...newException, slots: newException.slots.filter((_, i) => i !== index) })} disabled={newException.slots.length === 1} >🗑</button>
											</div>
										))}
									</div>
								</div>
							)}
							
							<div className="cal-form-actions">
								<button onClick={() => handleSaveException(newException)} className="cal-btn-save" disabled={saving === 'exception-new' || !!getDateError(newException.date)}>{saving === 'exception-new' ? "Enregistrement..." : "Enregistrer"}</button>
								<button onClick={() => setNewException(null)} className="cal-btn-cancel">Annuler</button>
							</div>
						</div>
					)}

					<div className="cal-exceptions-list">
						{exceptions.sort((a, b) => a.date.localeCompare(b.date)).map(exception => (
							<div key={exception.id} className="cal-exception-card">
								{editingException === exception.id ? (
									<div className="cal-exception-form">
										<div className="cal-form-grid">
											<div className="cal-form-field">
												<label>Date</label>
												<input
													type="date"
													value={exception.date}
													onChange={(e) => setExceptions(prev => 
														prev.map(ex => ex.id === exception.id ? { ...ex, date: e.target.value } : ex)
													)}
													className="cal-input"
													min={today}
												/>
												{getDateError(exception.date, exception.id) && (
													<small style={{ color: 'red', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
														{getDateError(exception.date, exception.id)}
													</small>
												)}
											</div>
											<div className="cal-form-field">
												<label>
													<input
														type="checkbox"
													checked={exception.slots === null}
													onChange={(e) => setExceptions(prev => 
														prev.map(ex => ex.id === exception.id ? { 
															...ex, 
															slots: e.target.checked ? null : [{ opens_at: "09:00", closes_at: "18:00" }]
														} : ex)
														)}
													/>
													Fermé
												</label>
											</div>
											<div className="cal-form-field cal-form-field-full">
												<label>Commentaire</label>
												<input
													type="text"
													value={exception.comment || ""}
													onChange={(e) => setExceptions(prev => 
														prev.map(ex => ex.id === exception.id ? { ...ex, comment: e.target.value } : ex)
													)}
													className="cal-input"
												/>
											</div>
										</div>
										
										{exception.slots !== null && (
											<div className="cal-exception-slots">
												<div className="cal-slots-header">
													<h4>Créneaux horaires</h4>
													<button type="button" className="cal-btn-add-slot" onClick={() => handleAddExceptionSlot(exception.id)}>+ Créneau</button>
												</div>
												<div className="cal-slots-list">
													{(exception.slots || []).map((slot, index) => (
													<div key={index} className="cal-slot-row">
														<span className="cal-slot-label">Créneau {index + 1}</span>
														{editingExceptionSlot === `${exception.id}-${index}` ? (
															<>
																<input
																	type="time"
																	value={slot.opens_at}
																	onChange={(e) => handleUpdateExceptionSlot(exception.id, index, 'opens_at', e.target.value)}
																	className="cal-input-time"
																/>
																<span>→</span>
																<input
																	type="time"
																	value={slot.closes_at}
																	onChange={(e) => handleUpdateExceptionSlot(exception.id, index, 'closes_at', e.target.value)}
																		className="cal-input-time"
																	/>
																	<button type="button" className="cal-btn-save-slot" onClick={() => setEditingExceptionSlot(null)}>✓</button>
																</>
														) : (
															<>
																<span className="cal-slot-time-display">{slot.opens_at} - {slot.closes_at}</span>
																<button type="button" className="cal-btn-edit-slot" onClick={() => setEditingExceptionSlot(`${exception.id}-${index}`)}>✎</button>
															</>
														)}
														<button type="button" className="cal-btn-delete-slot" onClick={() => handleDeleteExceptionSlot(exception.id, index)} disabled={exception.slots.length === 1} >🗑</button>
														</div>
													))}
												</div>
											</div>
										)}
										
										<div className="cal-form-actions">
											<button onClick={() => handleSaveException(exception)} className="cal-btn-save" disabled={saving === `exception-${exception.id}` || !!getDateError(exception.date, exception.id)} >
												{saving === `exception-${exception.id}` ? "..." : "Sauvegarder"}
											</button>
											<button onClick={() => { setEditingException(null); setEditingExceptionSlot(null); }} className="cal-btn-cancel" > Annuler</button>
										</div>
									</div>
								) : (
									<>
										<div className="cal-exception-info">
											<div className="cal-exception-date">
												{formatDateForDisplay(exception.date)}
											</div>
										{exception.slots === null ? (
												<div className="cal-exception-closed">Fermé</div>
											) : (
												<div className="cal-exception-hours-list">
													{(exception.slots || []).map((slot, index) => (
														<div key={index} className="cal-exception-hours">{slot.opens_at} - {slot.closes_at}</div>
													))}
												</div>
											)}
											{exception.comment && (
												<div className="cal-exception-comment">{exception.comment}</div>
											)}
										</div>
										<div className="cal-exception-actions">
											<button onClick={() => setEditingException(exception.id)} className="cal-btn-edit" >Modifier</button>
											<button onClick={() => handleDeleteException(exception.id)} className="cal-btn-delete" disabled={saving === `exception-${exception.id}`} >Supprimer</button>
										</div>
									</>
								)}
							</div>
						))}
						{exceptions.length === 0 && !newException && (
							<p className="cal-empty-list">Aucune exception définie</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}